import { CheckCircle2, LockKeyhole, Target } from "lucide-react";
import Button from "../components/Button";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";

const pricingPrinciples = [
  [
    Target,
    "Built around your outcome",
    "Every service is shaped around the scope, complexity, timing and level of coordination your outcome requires.",
  ],
  [
    CheckCircle2,
    "Clear before commitment",
    "You see what is included, the delivery path and the milestones that matter before work moves forward.",
  ],
  [
    LockKeyhole,
    "Secure from start to finish",
    "Payments, progress and supporting records are handled through a structured customer experience.",
  ],
];

export function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Clear scope. Transparent milestones. No surprises."
        copy="Pricing is shaped around the service you need and the outcome you want to achieve. The customer portal keeps the details, progress and payments together."
      />

      <section id="content" className="section-space bg-white">
        <div className="container">
          <SectionHeading
            eyebrow="Our pricing approach"
            title="A considered price for a clearly defined outcome."
            copy="The Bridge brings structure to complex services, so you can make an informed decision before execution begins."
            align="center"
          />
          <div className="mt-12 grid gap-8 border-y border-ink/10 md:grid-cols-3 md:divide-x md:divide-ink/10">
            {pricingPrinciples.map(([Icon, title, copy], index) => (
              <Reveal key={title} delay={index * 70}>
                <div className="py-8 md:px-8 first:md:pl-0 last:md:pr-0">
                  <Icon className="text-ocean" size={28} strokeWidth={2} />
                  <h2 className="mt-7 font-display text-xl font-bold text-ink">
                    {title}
                  </h2>
                  <p className="mt-3 max-w-xs text-sm leading-7 text-slate">
                    {copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space bg-mist">
        <div className="container grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <SectionHeading
            eyebrow="What shapes the price"
            title="The details matter."
            copy="Service pricing reflects the work required to deliver a dependable result—not a one-size-fits-all promise."
          />
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              "The service category and required expertise",
              "The locations, documents and dependencies involved",
              "The delivery timeline and level of coordination",
              "Any third-party costs or payments required to complete the work",
            ].map((item, index) => (
              <Reveal key={item} delay={index * 60}>
                <div className="flex gap-4 border-t border-ink/10 pt-5">
                  <CheckCircle2
                    className="mt-0.5 shrink-0 text-ocean"
                    size={22}
                  />
                  <p className="text-sm leading-7 text-ink">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space bg-ink text-white">
        <div className="container flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="eyebrow text-cyan">Ready to move forward?</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-[-.04em] sm:text-4xl">
              Continue in the customer portal.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-slate-300">
              Review available services, manage your account and keep every
              milestone in one secure place.
            </p>
          </div>
          <Button to="/login">Continue to portal</Button>
        </div>
      </section>
    </>
  );
}
